import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { ArrowLeft, ArrowRight, Sparkles } from 'lucide-react'
import { useSelector } from 'react-redux'
import { Button, CloseButton } from 'components/ui'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import { withAlpha } from 'utils'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'

interface IProps {
  /** Whether this subtask already went through the AI flow (`is_ai_enhanced`). Drives the whole look. */
  isAiEnhanced: boolean
  isDisabled?: boolean
  onGenerate: () => void
  /** Turns an AI subtask back into a plain photo one. Only reachable in the enhanced state. */
  onDisable: () => void
}

/**
 * The single AI affordance of a photo subtask, rendered in BOTH states — it replaced the
 * `is_ai_enhanced` switch, which is why the state has to be legible from the card itself and not only
 * from the camera icon above it:
 *
 * - plain photo   → muted grey card, outline sparkle, `secondary` CTA. An offer.
 * - AI subtask    → accent-tinted card, filled sparkle, solid accent CTA, plus an X to revert. Active.
 *
 * `is_ai_enhanced` is never toggled directly: it flips to true only when the user completes the AI flow
 * (`useVmAiEnhance.handleDone`), and back to false through `onDisable`. So the card always reflects
 * something the user actually did.
 *
 * Shared by both creation flows — the modal `Widgets/Image` and the full-page `SubtaskModal` — so this
 * feature cannot drift into two UIs. Only mounted for VM creation; outside VM there is no AI flow at all.
 */
export const AiEnhanceBanner: React.FC<IProps> = ({ isAiEnhanced, isDisabled = false, onGenerate, onDisable }) => {
  const theme = useSelector((state: RootState) => state.general.theme)
  const isRtl = useSelector(isRtlSelector)

  const accentColor = theme?.elementsColor || '#FF2D78'

  // Same copy in both states: enhanced reads as a statement, muted reads as an offer. No extra keys.
  const t_title = useLocaleText('ai_photo_title')
  const t_desc = useLocaleText('ai_photo_desc')
  const t_generate = useLocaleText('generate_prompt')

  const tint = isAiEnhanced ? accentColor : colors.greyDark

  return (
    <Flex
      w="100%"
      alignItems="center"
      borderRadius="12px"
      px="16px"
      py="12px"
      // Plain flex row — RTL mirrors it for free, no isRtl branching needed
      style={{
        columnGap: '12px',
        background: isAiEnhanced ? withAlpha(accentColor, 0.08) : colors.greyLight5,
        border: `1px solid ${isAiEnhanced ? withAlpha(accentColor, 0.2) : colors.greyBorder}`,
      }}
    >
      <Flex
        w="36px"
        h="36px"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        style={{ background: isAiEnhanced ? withAlpha(accentColor, 0.18) : 'white' }}
      >
        {/* Sparkle in both states — the card is about AI either way; only fill and tint differ */}
        <Sparkles size={17} color={tint} {...(isAiEnhanced ? { fill: accentColor } : {})} />
      </Flex>

      <Box flex="1" minW={0}>
        <Text fontSize="14px" fontWeight="700" lineHeight="1.35" color={isAiEnhanced ? colors.blackLight : tint}>
          {t_title}
        </Text>
        <Text fontSize="13px" lineHeight="1.35" color={colors.analyticGrey}>
          {t_desc}
        </Text>
      </Box>

      <Button
        variant={isAiEnhanced ? 'primary' : 'secondary'}
        h="36px"
        px="18px"
        fontSize="14px"
        fontWeight="700"
        borderRadius="10px"
        flexShrink={0}
        // Trailing arrow: the CTA leaves this card for the AI flow, it does not act in place.
        // `endIcon` is already the trailing side under `dir=rtl`; only the glyph has to flip.
        endIcon={isRtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
        {...(isAiEnhanced ? { bg: accentColor } : {})}
        // A snapshot taken mid-upload would capture blob placeholder URLs
        isDisabled={isDisabled}
        onClick={isDisabled ? undefined : onGenerate}
        {...(isAiEnhanced
          ? {
              _hover: { bg: accentColor, opacity: 0.88, transform: 'translateY(-1px)' },
              _active: { bg: accentColor, transform: 'scale(0.97)' },
              _focus: { boxShadow: `0 0 0 3px ${withAlpha(accentColor, 0.25)}`, outline: 'none' },
            }
          : {})}
      >
        {t_generate}
      </Button>

      {isAiEnhanced && <CloseButton flexShrink={0} minW="28px" h="28px" p="5px" onClick={onDisable} />}
    </Flex>
  )
}
